import { React, useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import pageStyles from "./ManufacturerPage.module.css";
import { EnhancedStockTable } from "../components/EnhancedStockTable/EnhancedStockTable";
import { DEFAULT_LISTING_SORT_MODEL } from "../utils/listingDefaults";
import StockFilters from "../components/StockFilters/StockFilters";
import { Snackbar, Alert, Typography, Button, Grid } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export const ManufacturerPage = () => {
    const navigate = useNavigate();
    const { stockManufacturer } = useParams();
    const normalizedManufacturer = stockManufacturer ? stockManufacturer.toUpperCase() : "";
    const [searchTerm, setSearchTerm] = useState("");
    const [additionalFilters, setAdditionalFilters] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { isAuthenticated, isPremium, login } = useAuth();
    
    const filters = useMemo(() => ({
        manufacturers: normalizedManufacturer ? [normalizedManufacturer] : undefined,
        ...additionalFilters
    }), [normalizedManufacturer, additionalFilters]);

    const manufacturerLabel = normalizedManufacturer ? `${normalizedManufacturer} GPUs` : 'GPUs';
    const ctaLabel = !isAuthenticated
        ? 'Sign in to track'
        : !isPremium
            ? 'Unlock tracking'
            : 'Manage tracking';

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleFiltersChange = useCallback((newFilters) => {
        setAdditionalFilters(newFilters);
    }, []);

    const handleClearFilters = useCallback(() => {
        setAdditionalFilters({});
    }, []);

    useEffect(() => {
        const handleShowSnackbar = (event) => {
            const { message, severity } = event.detail;
            setSnackbar({ open: true, message, severity });
        };

        window.addEventListener('show-snackbar', handleShowSnackbar);
        return () => window.removeEventListener('show-snackbar', handleShowSnackbar);
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handlePrimaryCta = () => {
        if (!isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', `${window.location.pathname}${window.location.search}`);
            login();
            return;
        }

        if (!isPremium) {
            navigate('/subscription');
            return;
        }

        navigate('/my-tracked-stocks');
    };

    return (
        <div className={pageStyles.page}>
            <Seo
                title={`${manufacturerLabel} Stock Tracker | StockMaid`}
                description={`Track ${manufacturerLabel} availability at Best Buy with real-time stock and pricing updates.`}
                canonicalPath={`/manufacturers/${normalizedManufacturer || ""}`}
            />
            <section className={pageStyles.hero}>
                <div className={pageStyles.heroText}>
                    <Typography variant="h4" component="h1" className={pageStyles.heroTitle}>
                        {manufacturerLabel}
                    </Typography>
                    <Typography variant="body1" className={pageStyles.heroSubtitle}>
                        Browse cards, compare availability, and set alerts the moment stock drops.
                    </Typography>
                </div>
                <div className={pageStyles.heroActions}>
                    <span className={pageStyles.heroPill}>Real-time alerts</span>
                    <span className={pageStyles.heroPill}>Retail stock checks</span>
                    <Button variant="contained" onClick={handlePrimaryCta}>
                        {ctaLabel}
                    </Button>
                </div>
            </section>

            <Grid container spacing={2} alignItems="flex-start">
                <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                        position: { md: 'sticky' },
                        top: { md: 112 },
                        alignSelf: { md: 'flex-start' }
                    }}
                >
            <StockFilters
                filters={additionalFilters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                onSearch={handleSearch}
                suggestionFilters={filters}
                showCategory={false}
                showManufacturer={false}
                showPrice={false}
                showAvailability={false}
                showDateRange={false}
                layout="sidebar"
            />
                </Grid>
                <Grid item xs={12} md={9}>
                    <EnhancedStockTable 
                        filters={filters}
                        searchTerm={searchTerm}
                        displayMode="cards"
                        initialSortModel={DEFAULT_LISTING_SORT_MODEL}
                    />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
